import { RowsPlusBottom } from "@phosphor-icons/react";

import {
  DropdownMenu,
  DropdownMenuCheckbox,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FilterButton from "common/components/ui/FilterButton";
import useCampaigns from "common/datahooks/useCampaigns";

interface CampaignSelectorProps {
  selectedCampaignIds: string[];
  setSelectedCampaignIds: (selectedCampaignIds: string[]) => void;
}

export default function CampaignSelector({
  selectedCampaignIds,
  setSelectedCampaignIds,
}: CampaignSelectorProps) {
  const { campaigns, isLoadingCampaigns } = useCampaigns();

  function removeCampaignId(campaignId: string) {
    setSelectedCampaignIds(
      selectedCampaignIds.filter((id) => id !== campaignId),
    );
  }

  function addCampaignId(campaignId: string) {
    setSelectedCampaignIds([...selectedCampaignIds, campaignId]);
  }

  const activeCampaigns = campaigns
    ? campaigns.filter(
        (campaign) => campaign.state !== "INIT" && campaign.state !== "CREATED",
      )
    : [];

  const hasCampaigns = activeCampaigns.length > 0;

  let triggerLabel = "All messages";

  if (isLoadingCampaigns) {
    triggerLabel = "Loading campaigns...";
  } else if (!hasCampaigns) {
    triggerLabel = "No campaigns available";
  } else if (
    selectedCampaignIds.length > 0 &&
    selectedCampaignIds.length !== activeCampaigns.length
  ) {
    triggerLabel =
      selectedCampaignIds.length > 1
        ? `${selectedCampaignIds.length} campaigns selected`
        : activeCampaigns.find(({ id }) => id === selectedCampaignIds[0])?.name;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        asChild
        disabled={!hasCampaigns || isLoadingCampaigns}
      >
        <FilterButton
          leftIcon={<RowsPlusBottom />}
          isLoading={isLoadingCampaigns}
        >
          {triggerLabel}
        </FilterButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="min-w-[--radix-popper-anchor-width] z-aboveDialog">
        <DropdownMenuList>
          <DropdownMenuItem
            onSelect={() => {
              setSelectedCampaignIds([]);
            }}
          >
            All messages
          </DropdownMenuItem>

          {activeCampaigns?.map(({ id, name }) => {
            const isCampaignIdIncluded = selectedCampaignIds.includes(id);

            return (
              <DropdownMenuItem
                key={id}
                onSelect={() => {
                  setSelectedCampaignIds([id]);
                }}
              >
                <DropdownMenuCheckbox
                  isChecked={isCampaignIdIncluded}
                  onCheckedChange={() => {
                    if (isCampaignIdIncluded) {
                      removeCampaignId(id);
                      return;
                    }

                    addCampaignId(id);
                  }}
                />

                {name}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
